import 'semantic-ui-css/semantic.min.css';
import './_App.scss';
import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorBoundary } from 'components/common';
import { Layout } from 'layouts/Layout';
import { appendAdobeAnalyticsScript } from 'libs/adobeAnalytics';
import { useNotificationScript } from 'controls/hooks/useNotificationScript';
import { ModalController } from 'controls/modals';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { DownloadRCM } from 'layouts/downloadScreen/downloadRcm';
import { News } from 'layouts/newsScreen/news';
import { NewsDetail } from 'layouts/newsScreen/detail/newsDetail';
import { LoginPage } from 'layouts/loginScreen/LoginPage';
import { AccountDetailPage } from 'layouts/accountDetailScreen/AccountDetailPage';
import { KeyPage } from 'layouts/keyScreen/KeyPage';
import { ThankYouKeyPage } from 'layouts/thankYouKeyScreen/ThankYouKeyPage';
import { HomePage } from 'layouts/homeScreen/HomePage';
import LoaderSpinner from 'components/atoms/Loader/LoaderSpinner';
import { useAuth } from 'context/AuthContext';
import { setUser } from 'app/api/userSlice';
import { ContactPage } from 'layouts/contactScreen/ContactPage';
import { NotFoundPage } from '@rcw/ui';

const TRACKING_ID = process.env.REACT_APP_GA4_TRACKING_ID;

export function App() {
  const notificationScript = useNotificationScript();
  const dispatch = useDispatch();
  const { user, isLoading } = useAuth();
  const modals = useSelector((state) => state.modals);

  useEffect(() => {
    appendAdobeAnalyticsScript();
    ReactGA.initialize(TRACKING_ID);
  }, []);

  useEffect(() => {
    if (user) {
      dispatch(setUser({
        email: user.profile.email,
        name: user.profile.name,
        token: user.id_token,
      }));
    } else if (!isLoading) {
      dispatch(setUser({ guest: true }));
    }
  }, [user, isLoading, dispatch]);

  return (
    <div className='App'>
      {notificationScript && (
        <Helmet>
          <script>{notificationScript}</script>
        </Helmet>
      )}
      <link rel='preconnect' href='https://fonts.gstatic.com' />
      <link
        href='https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap'
        rel='stylesheet'
      />
      <link
        href='https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap'
        rel='stylesheet'
      />
      <ErrorBoundary>
        <Routes>
          <Route path='/' element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path='callback' element={<LoaderSpinner />} />
            <Route path='download-rcm' element={<DownloadRCM />} />
            <Route path='contact-us' element={<ContactPage />} />
            <Route path='news' element={<News />} />
            <Route path='news/:id' element={<NewsDetail />} />
            <Route path='login' element={<LoginPage />} />
            <Route path='account-detail' element={<AccountDetailPage />} />
            <Route path='key' element={<KeyPage />} />
            <Route path='thank-you-key' element={<ThankYouKeyPage />} />
            <Route
              path='*'
              element={<NotFoundPage homeUrl={process.env.REACT_APP_HOME} />}
            />
          </Route>
        </Routes>
      </ErrorBoundary>
      <ModalController modals={modals} />
    </div>
  );
}
